<template>
  <div class="container-xxl py-4">
    <div class="d-flex align-items-baseline justify-content-between mb-3">
      <div>
        <h1 class="h2 mb-1">Report</h1>
        <p class="text-muted mb-0">Rekap total pembelian per customer.</p>
      </div>
    </div>

    <!-- FILTER -->
    <div class="card shadow-sm mb-4">
      <div class="card-body">
        <div class="row g-3 align-items-end">
          <div class="col-md-4 position-relative">
            <label class="text-muted small mb-1 d-block">Nama Customer</label>
            <input
              type="text"
              class="form-control"
              v-model="customerName"
              placeholder="Cari nama customer..."
              autocomplete="off"
              @focus="showDropdown = true"
              @input="showDropdown = true"
              @blur="showDropdown = false"
              @keyup.enter="fetchReport"
            >

            <div v-if="showDropdown && filteredCustomers.length" class="customer-dropdown">
              <div
                v-for="c in filteredCustomers"
                :key="c.id"
                class="customer-option"
                @mousedown.prevent="selectCustomer(c)"
              >
                <div class="customer-name">{{ c.billing_name }}</div>
                <div class="customer-sub">
                  {{ trimText(c.address) }}<span v-if="c.billing_phone"> — {{ c.billing_phone }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="col-md-3">
            <label class="text-muted small mb-1 d-block">Dari Tanggal</label>
            <input type="date" class="form-control" v-model="startDate">
          </div>

          <div class="col-md-3">
            <label class="text-muted small mb-1 d-block">Sampai Tanggal</label>
            <input type="date" class="form-control" v-model="endDate">
          </div>

          <div class="col-md-2">
            <button
              class="btn btn-primary w-100"
              @click="fetchReport"
              :disabled="!customerName || loading"
            >
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              Search
            </button>
          </div>
        </div>
      </div>
    </div>

    <div v-if="error" class="alert alert-danger mb-3">{{ error }}</div>

    <!-- EMPTY STATE (belum search) -->
    <div v-if="!hasSearched && !loading" class="text-muted text-center py-5">
      Pilih customer dan periode, lalu klik Search.
    </div>

    <!-- RESULT -->
    <template v-if="hasSearched">
      <!-- KPI -->
      <div class="kpi-grid mb-4">
        <div class="kpi-card">
          <div class="kpi-top">
            <div class="kpi-label">Total Transaksi</div>
            <div class="kpi-value">{{ summary.total_invoices }} Invoice</div>
          </div>
          <div class="kpi-footer muted">
            Periode {{ formatDate(startDate) }} - {{ formatDate(endDate) }}
          </div>
        </div>

        <div class="kpi-card">
          <div class="kpi-top">
            <div class="kpi-label">Total Pembelian</div>
            <div class="kpi-value">Rp{{ toIDR(summary.total_revenue) }}</div>
          </div>
          <div class="kpi-footer muted">
            {{ customerName }}
          </div>
        </div>
      </div>

      <!-- VARIAN TABLE -->
      <div class="card shadow-sm">
        <div class="card-header bg-white">
          <strong>Rincian Varian — {{ customerName }}</strong>
        </div>
        <div class="card-body p-0">
          <div v-if="loading" class="p-3">
            <div class="spinner-border spinner-border-sm text-secondary me-2"></div>
            <span class="text-muted">Memuat…</span>
          </div>
          <div v-else class="table-responsive">
            <table class="table mb-0 align-middle">
              <thead class="table-light">
                <tr>
                  <th style="width:56px;">No</th>
                  <th>Nama Varian</th>
                  <th class="text-end" style="width:120px;">QTY</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="items.length === 0">
                  <td colspan="3" class="text-muted text-center py-3">
                    Tidak ada transaksi pada periode ini.
                  </td>
                </tr>
                <tr v-for="(it,i) in items" :key="it.product_name + i">
                  <td class="text-muted">{{ i+1 }}</td>
                  <td>{{ it.product_name }}</td>
                  <td class="text-end">{{ it.qty }}</td>
                </tr>
              </tbody>
              <tfoot v-if="items.length">
                <tr>
                  <td colspan="2" class="text-end fw-bold">TOTAL QTY</td>
                  <td class="text-end fw-bold">{{ totalQty }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { api } from '@/utils/api';

export default {
  name: 'ReportPage',
  data() {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);

    return {
      customerName: '',
      customerList: [],
      showDropdown: false,
      startDate: firstDay.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
      loading: false,
      hasSearched: false,
      error: '',
      summary: { total_invoices: 0, total_revenue: 0 },
      items: [],
    };
  },
  computed: {
    totalQty() {
      return this.items.reduce((sum, it) => sum + Number(it.qty || 0), 0);
    },
    filteredCustomers() {
      const q = this.customerName.trim().toLowerCase();
      if (!q) return [];
      return this.customerList
        .filter(c => (c.billing_name || '').toLowerCase().includes(q))
        .slice(0, 8);
    },
  },
  mounted() {
    this.fetchCustomerList();
  },
  methods: {
    async fetchCustomerList() {
      try {
        const res = await fetch(api('customer/list.php'));
        const json = await res.json();
        if (json.success) this.customerList = json.data || [];
      } catch (e) {
        console.error(e);
      }
    },
    selectCustomer(c) {
      this.customerName = c.billing_name;
      this.showDropdown = false;
    },
    trimText(text, max = 28) {
      if (!text) return '-';
      return text.length > max ? text.slice(0, max) + '…' : text;
    },
    async fetchReport() {
      if (!this.customerName) return;

      try {
        this.loading = true;
        this.error = '';
        this.hasSearched = true;

        const qs = new URLSearchParams({
          customer: this.customerName,
          start_date: this.startDate,
          end_date: this.endDate,
        });

        const res = await fetch(api(`report_stats.php?${qs.toString()}`));
        const json = await res.json();

        if (json.success) {
          this.summary = json.summary || this.summary;
          this.items = json.items || [];
        } else {
          this.error = json.message || 'Gagal memuat data';
          this.items = [];
        }
      } catch (e) {
        console.error(e);
        this.error = 'Terjadi kesalahan jaringan';
      } finally {
        this.loading = false;
      }
    },
    toIDR(n) {
      return (Number(n) || 0).toLocaleString('id-ID');
    },
    formatDate(d) {
      if (!d) return '-';
      return new Date(d).toLocaleDateString('id-ID', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    },
  },
};
</script>

<style scoped>
/* ===== PAGE BASE ===== */
.container-xxl {
  background: #f8fafc;
  min-height: 100vh;
}

h1.h2 {
  font-weight: 650;
  letter-spacing: -0.4px;
}

.text-muted {
  color: #64748b !important;
}

/* ===== FILTER CARD ===== */
.card {
  border-radius: 20px;
  border: none;
  box-shadow: 0 6px 20px rgba(15,23,42,.05);
  transition: all .2s ease;
}

.card:hover {
  box-shadow: 0 12px 30px rgba(15,23,42,.08);
}

.card-header {
  border-bottom: 1px solid #f1f5f9;
  font-weight: 600;
  font-size: 15px;
  padding: 16px 20px;
}

.card-body {
  padding: 0;
}

.card-body.p-0 ~ .card-body,
.card > .card-body {
  padding: 20px;
}

/* ===== FORM CONTROLS ===== */
.form-control,
.form-select {
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  padding: 8px 14px;
  font-size: 14px;
  transition: all .15s ease;
}

.form-control:focus,
.form-select:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99,102,241,.12);
}

.btn-primary {
  border-radius: 12px;
  background: #6366f1;
  border: none;
  padding: 8px 14px;
  font-weight: 600;
}

.btn-primary:hover {
  background: #4f46e5;
}

.btn-primary:disabled {
  background: #a5a6f6;
}

/* ===== CUSTOMER DROPDOWN ===== */
.customer-dropdown {
  position: absolute;
  top: 100%;
  left: 12px;
  right: 12px;
  margin-top: 4px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 12px 30px rgba(15,23,42,.12);
  max-height: 260px;
  overflow-y: auto;
  z-index: 20;
}

.customer-option {
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
}

.customer-option:last-child {
  border-bottom: none;
}

.customer-option:hover {
  background: #f8fafc;
}

.customer-name {
  font-weight: 700;
  font-size: 14px;
  color: #0f172a;
}

.customer-sub {
  font-size: 12.5px;
  color: #64748b;
  margin-top: 2px;
}

/* ===== KPI GRID ===== */
.kpi-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 20px;
}

.kpi-card {
  background: #ffffff;
  border-radius: 20px;
  padding: 24px;
  box-shadow: 0 6px 20px rgba(15,23,42,.05);
  transition: .25s ease;
}

.kpi-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 32px rgba(15,23,42,.08);
}

.kpi-top {
  margin-bottom: 16px;
}

.kpi-label {
  font-size: .75rem;
  text-transform: uppercase;
  letter-spacing: .08em;
  color: #64748b;
  margin-bottom: 6px;
}

.kpi-value {
  font-size: 1.8rem;
  font-weight: 700;
  letter-spacing: -.5px;
  color: #0f172a;
}

.kpi-footer {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.kpi-footer.muted {
  font-size: 14px;
  color: #64748b;
}

/* ===== TABLE STYLE ===== */
.table {
  font-size: 14px;
}

.table thead {
  background: #f1f5f9;
}

.table th {
  font-weight: 600;
  font-size: 13px;
  color: #334155;
  padding: 14px 18px;
  border-bottom: 1px solid #e2e8f0;
}

.table td {
  padding: 14px 18px;
  border-top: 1px solid #f1f5f9;
  color: #475569;
}

.table tbody tr {
  transition: background .2s ease;
}

.table tbody tr:hover {
  background: #f8fafc;
}

.table tfoot td {
  background: #f8fafc;
  border-top: 2px solid #e2e8f0;
  color: #0f172a;
}

/* ===== SPINNER ===== */
.spinner-border {
  width: 1rem;
  height: 1rem;
}

/* ===== ALERT ===== */
.alert-danger {
  border-radius: 14px;
  border: none;
  background: rgba(220,38,38,.08);
  color: #dc2626;
}

/* ===== RESPONSIVE ===== */
@media (max-width: 768px) {
  .kpi-value {
    font-size: 1.3rem;
  }

  .card-header {
    font-size: 14px;
  }

  .table th,
  .table td {
    padding: 10px 12px;
  }
}
</style>